import { useState } from "react";
import { EnvelopeSimple, MapPin, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { EkamSiteFooter, EkamSiteHeader } from "../components/EkamSite";
import { AskEkamWidget } from "../components/AskEkam";

export default function EkamContact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await api.post("/enquiries", { ...form, organization: "", service: "" });
      toast.success("Message sent. Our team will reply within one business day.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send your message. Please email contact@anxya.tech.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#041718] text-slate-300" data-testid="ekam-contact-page">
      <EkamSiteHeader title="Contact" />
      <main className="mx-auto max-w-4xl px-6 pb-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-cyan-300">Contact Us</p>
        <h1 className="mt-4 font-display text-4xl font-light text-white sm:text-5xl">We would love to hear from you.</h1>

        <div className="mt-8 grid gap-4 sm:grid-cols-2">
          <div className="flex items-start gap-3 rounded-2xl border border-teal-500/10 bg-black/20 p-4">
            <MapPin size={20} className="mt-0.5 shrink-0 text-cyan-300" />
            <div>
              <p className="text-sm font-medium text-slate-200">Headquarters</p>
              <p className="mt-1 text-sm text-slate-500" data-testid="ekam-contact-hq">Pune, Maharashtra, India</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-teal-500/10 bg-black/20 p-4">
            <EnvelopeSimple size={20} className="mt-0.5 shrink-0 text-cyan-300" />
            <div>
              <p className="text-sm font-medium text-slate-200">Email</p>
              <a href="mailto:contact@anxya.tech" data-testid="ekam-contact-email-link" className="mt-1 block text-sm text-cyan-300 hover:text-cyan-200">contact@anxya.tech</a>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4 rounded-3xl border border-teal-500/10 bg-white/[0.02] p-6 sm:p-8" data-testid="ekam-contact-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name" data-testid="ekam-contact-name-input" className="h-12 border-teal-500/15 bg-[#0a2527] text-white placeholder:text-slate-600" />
            <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Your email" data-testid="ekam-contact-email-input" className="h-12 border-teal-500/15 bg-[#0a2527] text-white placeholder:text-slate-600" />
          </div>
          <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?" rows={5} data-testid="ekam-contact-message-input" className="w-full resize-none rounded-xl border border-teal-500/15 bg-[#0a2527] px-4 py-3 text-sm text-white outline-none transition-colors placeholder:text-slate-600 focus:border-cyan-400/40" />
          <Button type="submit" disabled={sending} data-testid="ekam-contact-submit-btn" className="rounded-full bg-cyan-400 px-8 text-slate-950 hover:bg-cyan-300 disabled:opacity-40">
            <PaperPlaneTilt size={16} className="mr-2" /> {sending ? "Sending…" : "Send Message"}
          </Button>
        </form>
      </main>
      <EkamSiteFooter />
      <AskEkamWidget />
    </div>
  );
}
